import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MediaFile } from '../store/media';
import { FORMAT_SPECS, FormatSpec, Platform, PostType } from '../config/formatSpecs';

interface PostPreviewProps {
  text: string;
  platforms: string[];
  media: MediaFile[];
  scheduledFor?: string;
  selectedPostType?: PostType;
}

// Ermittle das passende Meta-Format basierend auf Plattform, Post-Typ und Medientyp
function getMetaFormat(
  platform: Platform,
  postType: PostType,
  hasVideo: boolean,
  mediaCount: number
): FormatSpec | undefined {
  // Bei mehreren Medien -> Carousel
  if (mediaCount > 1) {
    return FORMAT_SPECS.find(
      (s) => s.platform === platform && s.postType === 'carousel'
    );
  }

  // Reel oder Story
  if (postType === 'reel' || postType === 'story') {
    return FORMAT_SPECS.find(
      (s) => s.platform === platform && s.postType === postType
    );
  }

  // Feed: Video oder Bild
  const mediaType = hasVideo ? 'video' : 'image';
  return FORMAT_SPECS.find(
    (s) =>
      s.platform === platform &&
      s.postType === 'feed' &&
      (s.mediaType === mediaType || s.mediaType === 'both')
  );
}

interface PlatformView {
  platform: Platform;
  label: string;
  format: FormatSpec | undefined;
}

export function PostPreview({ text, platforms, media, scheduledFor, selectedPostType = 'feed' }: PostPreviewProps) {
  const { t } = useTranslation(['posts', 'common']);
  const [currentIndex, setCurrentIndex] = useState(0);

  const hasVideo = media.some((m) => m.mime_type.startsWith('video/'));

  // Erstelle die Liste der verfügbaren Ansichten
  const views = useMemo<PlatformView[]>(() => {
    const result: PlatformView[] = [];

    if (platforms.includes('facebook')) {
      const format = getMetaFormat('facebook', selectedPostType, hasVideo, media.length);
      result.push({
        platform: 'facebook',
        label: 'Facebook',
        format,
      });
    }

    if (platforms.includes('instagram')) {
      const format = getMetaFormat('instagram', selectedPostType, hasVideo, media.length);
      result.push({
        platform: 'instagram',
        label: 'Instagram',
        format,
      });
    }

    return result;
  }, [platforms, selectedPostType, hasVideo, media.length]);

  // Keine Plattform ausgewählt
  if (views.length === 0) {
    return (
      <div className="text-center text-gray-500 py-8">
        {t('posts:preview.selectPlatform')}
      </div>
    );
  }

  // Korrigiere Index wenn nötig
  const safeIndex = Math.min(currentIndex, views.length - 1);
  const currentView = views[safeIndex];

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? views.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === views.length - 1 ? 0 : prev + 1));
  };

  return (
    <div className="space-y-3">
      {/* Navigation Header */}
      {views.length > 1 && (
        <div className="flex items-center justify-between bg-gray-50 rounded-lg p-2">
          <button
            onClick={handlePrev}
            className="p-1 rounded hover:bg-gray-200 transition-colors"
            title={t('posts:preview.previousView')}
          >
            <ChevronLeftIcon className="w-5 h-5 text-gray-600" />
          </button>

          <div className="text-center">
            <span className="text-sm font-medium text-gray-700">
              {currentView.label}
            </span>
            {currentView.format && (
              <p className="text-xs text-gray-500">
                {currentView.format.nameDe}
              </p>
            )}
            <div className="flex justify-center gap-1 mt-1">
              {views.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentIndex(idx)}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    idx === safeIndex ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>
          </div>

          <button
            onClick={handleNext}
            className="p-1 rounded hover:bg-gray-200 transition-colors"
            title={t('posts:preview.nextView')}
          >
            <ChevronRightIcon className="w-5 h-5 text-gray-600" />
          </button>
        </div>
      )}

      {/* Format-Info bei nur einer Plattform */}
      {views.length === 1 && currentView.format && (
        <div className="bg-gray-50 rounded-lg p-2 text-center">
          <span className="text-sm font-medium text-gray-700">
            {currentView.format.nameDe}
          </span>
          <span className="text-xs text-gray-500 ml-2">
            ({currentView.format.dimensions.width}×{currentView.format.dimensions.height})
          </span>
        </div>
      )}

      {/* Preview Content */}
      {currentView.platform === 'facebook' && (
        <FacebookPreview
          text={text}
          media={media}
          scheduledFor={scheduledFor}
          format={currentView.format}
        />
      )}
      {currentView.platform === 'instagram' && (
        <InstagramPreview
          text={text}
          media={media}
          scheduledFor={scheduledFor}
          format={currentView.format}
        />
      )}
    </div>
  );
}

function ChevronLeftIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
    </svg>
  );
}

function ChevronRightIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  );
}

function FacebookPreview({
  text,
  media,
  scheduledFor,
  format: _format,
}: {
  text: string;
  media: MediaFile[];
  scheduledFor?: string;
  format?: FormatSpec;
}) {
  const { t } = useTranslation(['posts', 'common']);
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const isCarousel = media.length > 1;

  const handlePrevMedia = () => {
    setCurrentMediaIndex((prev) => (prev === 0 ? media.length - 1 : prev - 1));
  };

  const handleNextMedia = () => {
    setCurrentMediaIndex((prev) => (prev === media.length - 1 ? 0 : prev + 1));
  };

  // Facebook zeigt bei Carousels horizontale Scroll-Ansicht
  const renderMedia = () => {
    if (media.length === 0) return null;

    // Single image/video
    if (media.length === 1) {
      const m = media[0];
      return (
        <div className="relative">
          {m.mime_type.startsWith('image/') ? (
            <img src={m.url} alt="" className="w-full max-h-80 object-cover" />
          ) : (
            <div className="w-full h-64 bg-gray-900 flex items-center justify-center">
              <svg className="w-16 h-16 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" />
              </svg>
            </div>
          )}
        </div>
      );
    }

    // Carousel (2+ images) - Facebook style horizontal cards
    return (
      <div className="relative">
        {/* Horizontal scroll container */}
        <div className="flex gap-2 overflow-x-auto pb-2 snap-x snap-mandatory scrollbar-hide">
          {media.map((m, idx) => (
            <div
              key={m.id}
              className={`flex-shrink-0 w-[85%] snap-center relative rounded-lg overflow-hidden ${
                idx === currentMediaIndex ? 'ring-2 ring-blue-500' : ''
              }`}
              onClick={() => setCurrentMediaIndex(idx)}
            >
              {m.mime_type.startsWith('image/') ? (
                <img src={m.url} alt="" className="w-full h-48 object-cover" />
              ) : (
                <div className="w-full h-48 bg-gray-900 flex items-center justify-center">
                  <svg className="w-12 h-12 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </div>
              )}
              {/* Card number indicator */}
              <div className="absolute bottom-2 right-2 bg-black bg-opacity-60 text-white text-xs px-2 py-0.5 rounded">
                {idx + 1}/{media.length}
              </div>
            </div>
          ))}
        </div>

        {/* Navigation arrows for larger screens */}
        {isCarousel && (
          <>
            <button
              onClick={handlePrevMedia}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white bg-opacity-90 rounded-full flex items-center justify-center shadow-lg hover:bg-opacity-100 transition-all z-10"
            >
              <svg className="w-4 h-4 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={handleNextMedia}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white bg-opacity-90 rounded-full flex items-center justify-center shadow-lg hover:bg-opacity-100 transition-all z-10"
            >
              <svg className="w-4 h-4 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="border rounded-lg overflow-hidden bg-white shadow-sm">
      {/* Header */}
      <div className="flex items-center gap-2 p-3 border-b bg-gray-50">
        <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
        </svg>
        <span className="font-medium text-sm">{t('posts:preview.facebookPreview')}</span>
        {scheduledFor && (
          <span className="text-xs text-gray-500 ml-auto">
            {new Date(scheduledFor).toLocaleDateString('de-DE', {
              day: '2-digit',
              month: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        )}
      </div>

      {/* Post Content */}
      <div className="bg-white">
        {/* Author Line */}
        <div className="flex items-center gap-3 p-3">
          <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
            <span className="text-white font-bold text-sm">P</span>
          </div>
          <div className="flex-1">
            <p className="font-semibold text-[15px] text-gray-900">{t('posts:preview.pageName')}</p>
            <div className="flex items-center gap-1 text-xs text-gray-500">
              <span>{t('posts:preview.justNow')}</span>
              <span>·</span>
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 16 16">
                <path d="M8 0a8 8 0 1 0 0 16A8 8 0 0 0 8 0zM4.5 7.5a.5.5 0 0 1 0-1h7a.5.5 0 0 1 0 1h-7z"/>
                <path d="M8 1a7 7 0 1 1 0 14A7 7 0 0 1 8 1zm0 1a6 6 0 1 0 0 12A6 6 0 0 0 8 2z"/>
              </svg>
            </div>
          </div>
          <button className="text-gray-400 hover:bg-gray-100 p-1 rounded">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path d="M6 10a2 2 0 11-4 0 2 2 0 014 0zM12 10a2 2 0 11-4 0 2 2 0 014 0zM16 12a2 2 0 100-4 2 2 0 000 4z" />
            </svg>
          </button>
        </div>

        {/* Text */}
        {text && (
          <div className="px-3 pb-3">
            <p className="text-[15px] text-gray-900 whitespace-pre-wrap">{text}</p>
          </div>
        )}

        {/* Media */}
        {renderMedia()}

        {/* Reactions count (placeholder) */}
        <div className="px-3 py-2 flex items-center justify-between text-xs text-gray-500 border-b">
          <div className="flex items-center gap-1">
            <div className="flex -space-x-1">
              <span className="w-[18px] h-[18px] rounded-full bg-blue-500 flex items-center justify-center">
                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 10.5a1.5 1.5 0 113 0v6a1.5 1.5 0 01-3 0v-6zM6 10.333v5.43a2 2 0 001.106 1.79l.05.025A4 4 0 008.943 18h5.416a2 2 0 001.962-1.608l1.2-6A2 2 0 0015.56 8H12V4a2 2 0 00-2-2 1 1 0 00-1 1v.667a4 4 0 01-.8 2.4L6.8 7.933a4 4 0 00-.8 2.4z" />
                </svg>
              </span>
              <span className="w-[18px] h-[18px] rounded-full bg-red-500 flex items-center justify-center">
                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                </svg>
              </span>
            </div>
          </div>
        </div>

        {/* Engagement Bar */}
        <div className="flex justify-around items-center py-1 text-gray-500 text-sm font-medium">
          <button className="flex items-center gap-2 py-2 px-4 hover:bg-gray-100 rounded-md transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
            </svg>
            <span>{t('posts:preview.like')}</span>
          </button>
          <button className="flex items-center gap-2 py-2 px-4 hover:bg-gray-100 rounded-md transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            <span>{t('posts:preview.comment')}</span>
          </button>
          <button className="flex items-center gap-2 py-2 px-4 hover:bg-gray-100 rounded-md transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
            </svg>
            <span>{t('posts:preview.share')}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

function InstagramPreview({
  text,
  media,
  scheduledFor,
  format: _format,
}: {
  text: string;
  media: MediaFile[];
  scheduledFor?: string;
  format?: FormatSpec;
}) {
  const { t } = useTranslation(['posts', 'common']);
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const currentMedia = media[currentMediaIndex];
  const isCarousel = media.length > 1;

  const handlePrevMedia = () => {
    setCurrentMediaIndex((prev) => (prev === 0 ? media.length - 1 : prev - 1));
  };

  const handleNextMedia = () => {
    setCurrentMediaIndex((prev) => (prev === media.length - 1 ? 0 : prev + 1));
  };

  return (
    <div className="border rounded-lg overflow-hidden bg-white max-w-sm mx-auto">
      {/* Header */}
      <div className="flex items-center gap-2 p-3 border-b bg-gray-50">
        <svg className="w-5 h-5 text-pink-600" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073z" />
        </svg>
        <span className="font-medium text-sm">{t('posts:preview.instagramPreview')}</span>
        {scheduledFor && (
          <span className="text-xs text-gray-500 ml-auto">
            {new Date(scheduledFor).toLocaleDateString('de-DE', {
              day: '2-digit',
              month: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        )}
      </div>

      {/* Author Line */}
      <div className="flex items-center gap-2 p-3">
        <div className="w-8 h-8 bg-gradient-to-br from-purple-600 to-pink-500 rounded-full flex items-center justify-center">
          <span className="text-white font-bold text-xs">P</span>
        </div>
        <span className="font-medium text-sm">account_name</span>
      </div>

      {/* Media */}
      <div className="aspect-square bg-gray-100 relative">
        {currentMedia ? (
          currentMedia.mime_type.startsWith('image/') ? (
            <img
              src={currentMedia.url}
              alt=""
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-gray-900 flex items-center justify-center">
              <svg className="w-16 h-16 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" />
              </svg>
            </div>
          )
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400">
            <div className="text-center">
              <svg className="w-16 h-16 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <p className="text-sm">{t('posts:preview.imageRequired')}</p>
            </div>
          </div>
        )}

        {/* Carousel Navigation */}
        {isCarousel && (
          <>
            {/* Counter */}
            <div className="absolute top-2 right-2 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded-full">
              {currentMediaIndex + 1}/{media.length}
            </div>

            {/* Navigation Buttons */}
            <button
              onClick={handlePrevMedia}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white bg-opacity-90 rounded-full flex items-center justify-center shadow-lg hover:bg-opacity-100 transition-all"
            >
              <svg className="w-4 h-4 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={handleNextMedia}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white bg-opacity-90 rounded-full flex items-center justify-center shadow-lg hover:bg-opacity-100 transition-all"
            >
              <svg className="w-4 h-4 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>

            {/* Dots */}
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1">
              {media.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentMediaIndex(idx)}
                  className={`w-1.5 h-1.5 rounded-full transition-all ${
                    idx === currentMediaIndex ? 'bg-blue-500 w-2' : 'bg-white bg-opacity-70'
                  }`}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {/* Actions */}
      <div className="p-3 flex justify-between">
        <div className="flex gap-4">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
          </svg>
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
          </svg>
        </div>
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
        </svg>
      </div>

      {/* Caption */}
      <div className="px-3 pb-3">
        <p className="text-sm">
          <span className="font-medium">account_name </span>
          <span className="whitespace-pre-wrap">{text || t('posts:preview.descriptionPlaceholder')}</span>
        </p>
        <p className="text-xs text-gray-400 mt-1">{t('posts:preview.justNow')}</p>
      </div>
    </div>
  );
}
